function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

/* ===================================== */
/* SOFT PAGE TRANSITION                 */
/* ===================================== */

document.addEventListener("DOMContentLoaded", function () {
  const main = document.getElementById("MainContent");
  if (main) main.classList.add("is-visible");

  let transitionTimeout = null;
  let isNavigating = false;

  window.addEventListener("pageshow", function (e) {
    isNavigating = false;
    if (transitionTimeout) clearTimeout(transitionTimeout);
    if (main) {
      main.classList.remove("is-leaving");
      main.classList.add("is-visible");
    }
    if (loader) {
      loader.classList.remove("loading");
      loader.style.width = "0%";
    }
  });

  document.addEventListener("click", function (e) {
    if (isNavigating) return;
    const link = e.target.closest("a");
    if (!link) return;

    const url = link.getAttribute("href");

    if (
      url &&
      url.indexOf("#") !== 0 &&
      url.indexOf("javascript:") !== 0 &&
      link.target !== "_blank" &&
      !link.hasAttribute("download") &&
      link.hostname === window.location.hostname &&
      url !== window.location.href
    ) {
      e.preventDefault();
      isNavigating = true;

      if (main) {
        main.classList.remove("is-visible");
        main.classList.add("is-leaving");
      }

      transitionTimeout = setTimeout(function () {
        window.location.assign(url);
      }, 400);
    }
  });
});


/* ===================================== */
/* MINIMAL TOP LOADER                   */
/* ===================================== */

const loader = document.getElementById("page-loader");

document.addEventListener("click", function (e) {
  if (!loader) return;
  const link = e.target.closest("a");
  if (!link) return;

  const url = link.getAttribute("href");

  if (
    url &&
    url.indexOf("#") !== 0 &&
    url.indexOf("javascript:") !== 0 &&
    link.target !== "_blank" &&
    link.hostname === window.location.hostname
  ) {
    loader.classList.add("loading");
  }
});

window.addEventListener("load", function () {
  if (!loader) return;
  loader.classList.remove("loading");
  loader.classList.add("loaded");

  setTimeout(() => {
    loader.style.width = "0%";
    loader.classList.remove("loaded");
  }, 300);
});

/* ===================================== */
/* WAIT FOR MAIN PRODUCT IMAGE ONLY     */
/* ===================================== */

document.addEventListener("DOMContentLoaded", function () {

  const product = document.querySelector(".product");
  if (!product) return;

  const mainImage = product.querySelector(".product__media img");

  if (!mainImage) {
    product.classList.add("is-ready");
    return;
  }

  function reveal() {
    product.classList.add("is-ready");
  }

  if (mainImage.complete) {
    reveal();
  } else {
    mainImage.addEventListener("load", reveal);
    mainImage.addEventListener("error", reveal);
  }

  /* Safety fallback in case lazy loading never fires */
  setTimeout(reveal, 1200);
});


/* ===================================== */
/* DELAY HEADER SHRINK/EXPAND ON LOAD   */
/* ===================================== */

document.addEventListener("DOMContentLoaded", function () {
  const header = document.querySelector(".header-wrapper");
  if (!header) return;

  // Temporarily prevent automatic expand
  header.classList.add("force-compact");

  // Wait until fade animation finishes before letting it expand
  setTimeout(() => {
    header.classList.remove("force-compact");

    // Trigger scroll handler to adjust header properly
    window.dispatchEvent(new Event("scroll"));
  }, 500); // match your page fade timing
});

/* =========================
   PRODUCT ART VIEWER
========================= */

document.addEventListener("DOMContentLoaded", () => {

  const images = document.querySelectorAll(".product__media img");
  if (!images.length) return;

  let currentIndex = 0;

  function createDots(viewer) {
    if (images.length <= 1) return;
    const dots = document.createElement("div");
    dots.className = "viewer-dots";
    images.forEach((_, i) => {
      const dot = document.createElement("span");
      dot.className = "viewer-dot";
      if (i === currentIndex) dot.classList.add("active");
      dots.appendChild(dot);
    });
    viewer.appendChild(dots);
  }

  function updateDots(viewer) {
    const dots = viewer.querySelectorAll(".viewer-dot");
    dots.forEach((dot, i) => {
      dot.classList.toggle("active", i === currentIndex);
    });
  }

  function openViewer(index) {

    currentIndex = index;

    const viewer = document.createElement("div");
    viewer.className = "art-viewer";

    const img = document.createElement("img");
    img.src = images[currentIndex].src;

    const close = document.createElement("button");
    close.className = "viewer-close";
    close.innerHTML = "×";

    /* arrows — only shown if multiple images */
    const prevBtn = document.createElement("button");
    prevBtn.className = "viewer-arrow viewer-arrow--prev";
    prevBtn.innerHTML = "&#8592;";
    prevBtn.setAttribute("aria-label", "Previous image");

    const nextBtn = document.createElement("button");
    nextBtn.className = "viewer-arrow viewer-arrow--next";
    nextBtn.innerHTML = "&#8594;";
    nextBtn.setAttribute("aria-label", "Next image");

    function updateArrows() {
      prevBtn.style.opacity = currentIndex === 0 ? "0.2" : "1";
      nextBtn.style.opacity = currentIndex === images.length - 1 ? "0.2" : "1";
    }

    function goTo(newIndex) {
      if (newIndex < 0 || newIndex > images.length - 1) return;
      currentIndex = newIndex;
      img.src = images[currentIndex].src;
      updateDots(viewer);
      updateArrows();
    }

      prevBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      startX = 0;
      currentX = 0;
      goTo(currentIndex - 1);
    });

  nextBtn.addEventListener("click", () => {
  const { card } = cardEls[topIndex];
  card.style.transition = "transform 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.25s ease";
requestAnimationFrame(() => {
  card.style.transform = "translate(-40px, -50px) rotate(-15deg) scale(0.85)";
  card.style.opacity   = "0";
});
  setTimeout(() => {
    topIndex = (topIndex + 1) % 4;
    applyFan(true);
  }, 180);
});

    viewer.appendChild(img);
    viewer.appendChild(close);
    if (images.length > 1) {
      viewer.appendChild(prevBtn);
      viewer.appendChild(nextBtn);
    }

    createDots(viewer);
    document.body.appendChild(viewer);
    document.body.style.overflow = "hidden";

    requestAnimationFrame(() => {
      viewer.classList.add("active");
      updateArrows();
    });

  

    /* keyboard navigation */
    function onKeyDown(e) {
      if (e.key === "ArrowLeft") goTo(currentIndex - 1);
      if (e.key === "ArrowRight") goTo(currentIndex + 1);
      if (e.key === "Escape") closeViewer();
    }
    document.addEventListener("keydown", onKeyDown);

let startX = 0;
    let currentX = 0;
    let touchStartedOnViewer = false;

    viewer.addEventListener("touchstart", e => {
      if (e.target === prevBtn || e.target === nextBtn || e.target === close) {
        touchStartedOnViewer = false;
        return;
      }
      touchStartedOnViewer = true;
      startX = e.touches[0].clientX;
      currentX = e.touches[0].clientX;
    });

    viewer.addEventListener("touchmove", e => {
      if (!touchStartedOnViewer) return;
      currentX = e.touches[0].clientX;
      let diff = currentX - startX;
      if ((currentIndex === 0 && diff > 0) ||
          (currentIndex === images.length - 1 && diff < 0)) {
        diff *= 0.35;
      }
      img.style.transform = `translateX(${diff}px)`;
    });

    viewer.addEventListener("touchend", () => {
      if (!touchStartedOnViewer) return;
      const diff = currentX - startX;
      img.style.transition = "transform .25s ease";
      if (diff > 80 && currentIndex > 0) currentIndex--;
      else if (diff < -80 && currentIndex < images.length - 1) currentIndex++;
      img.src = images[currentIndex].src;
      img.style.transform = "translateX(0)";
      updateDots(viewer);
      updateArrows();
      setTimeout(() => { img.style.transition = ""; }, 250);
      touchStartedOnViewer = false;
    });

    function closeViewer() {
      viewer.classList.remove("active");
      document.removeEventListener("keydown", onKeyDown);
      setTimeout(() => {
        document.body.style.overflow = "";
        viewer.remove();
      }, 250);
    }

    close.addEventListener("click", closeViewer);
    viewer.addEventListener("click", e => {
      if (e.target === viewer) closeViewer();
    });
  }

  images.forEach((image, index) => {
    image.addEventListener("click", () => openViewer(index));
  });

});

/* ===================================== */
/* MTG TOKEN CARD VIEWER                */
/* ===================================== */

document.addEventListener("DOMContentLoaded", function () {

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  const productEl = document.querySelector("[data-product-id]");
  if (!productEl) return;

  const productType = document.querySelector('meta[name="product-type"]')?.content?.toLowerCase();
  if (productType !== "token") return;

  document.querySelector(".product")?.closest(".shopify-section")
    ?.querySelector(".product")?.classList.add("token-product-page");
  document.body.classList.add("token-product-page");

  const allImages = Array.from(document.querySelectorAll(".product__media img"))
    .map(img => img.src.replace(/&width=\d+/, "&width=800"));

  if (allImages.length < 2) return;

  const images    = allImages.slice(1);
  const frontMask = allImages[3] || null;
  const backMask  = allImages[4] || null;

  const allMediaItems = document.querySelectorAll(
    ".product__media-list .product__media-item, .product__media-list li"
  );
  if (allMediaItems[0]) allMediaItems[0].style.display = "none";

  let isFlipped = false;
  let isFoil    = false;

  /* ── helpers ─────────────────────────────────── */

  function applyMask(flipped) {
    const mask = flipped ? backMask : frontMask;
    if (mask) {
      holo.style.webkitMaskImage = `url(${mask})`;
      holo.style.maskImage       = `url(${mask})`;
      holo.style.webkitMaskSize  = "100% 100%";
      holo.style.maskSize        = "100% 100%";
      holo.style.webkitMaskMode  = "alpha";
      holo.style.maskMode        = "alpha";
      holo.style.transform       = flipped ? "scaleX(-1)" : "scaleX(1)";
    } else {
      holo.style.webkitMaskImage = "none";
      holo.style.maskImage       = "none";
      holo.style.transform       = "scaleX(1)";
    }
  }

  function readVariantFromDOM() {
    const radio = document.querySelector(
      ".product-form__input input[type='radio']:checked"
    );
    if (radio) return (radio.value || radio.dataset.value || "").toLowerCase();
    const checked = document.querySelector(
      ".product-form__input .swatch-input--checked, .product-form__input [aria-checked='true']"
    );
    if (checked) return (checked.textContent?.trim() || "").toLowerCase();
    return null;
  }

  function setVariantInDOM(target) {
    const radios = document.querySelectorAll(
      ".product-form__input input[type='radio']"
    );
    for (const r of radios) {
      const val = (r.value || r.dataset.value || "").toLowerCase();
      if (val === target) {
       if (!r.checked) {
  r.checked = true;
  const origFocus = r.focus.bind(r);
  r.focus = (opts) => origFocus({ ...(opts || {}), preventScroll: true });
  setTimeout(() => { r.focus = origFocus; }, 500);
  r.dispatchEvent(new Event("change", { bubbles: true }));
}
        return;
      }
    }
    const swatches = document.querySelectorAll(
      ".product-form__input [data-value], .product-form__input .swatch-input"
    );
    for (const s of swatches) {
      const val = (
        s.dataset.value ||
        s.getAttribute("aria-label") ||
        s.textContent?.trim() ||
        ""
      ).toLowerCase();
      if (val === target) {
  const origFocus = s.focus.bind(s);
  s.focus = (opts) => origFocus({ ...(opts || {}), preventScroll: true });
  setTimeout(() => { s.focus = origFocus; }, 500);
  s.click();
  return;
}
    }
  }

  /* Dispatch an event while preventing any scroll side-effects.
     Shopify's variant JS (and some theme JS) can call scrollIntoView /
     focus on the target element after a change, which jumps the page on
     mobile. We temporarily override those methods on the element so
     they become no-ops for the duration of the dispatch. */
  /* Dispatch an event while freezing window scroll for 400ms.
     Shopify's variant JS can trigger scrollIntoView / focus on many
     different elements (the radio, the swatch label, the ATC button),
     so patching just the target element isn't enough on mobile.
     Instead we intercept the relevant prototypes globally for the
     duration of the dispatch + any async callbacks it fires. */
  function dispatchNoScroll(el, event) {
    const scrollEl = document.querySelector('.gradient');
    const savedY = scrollEl ? scrollEl.scrollTop : window.scrollY;
    let locked = true;

    const origWindowScrollTo      = window.scrollTo.bind(window);
    const origScrollIntoView      = Element.prototype.scrollIntoView;
    const origFocus               = HTMLElement.prototype.focus;

    window.scrollTo = () => {};

    Element.prototype.scrollIntoView = function() {};

    HTMLElement.prototype.focus = function(opts) {
      origFocus.call(this, { ...opts, preventScroll: true });
    };

    el.dispatchEvent(event);

    // Restore prototypes immediately after synchronous dispatch
    window.scrollTo                   = origWindowScrollTo;
    Element.prototype.scrollIntoView  = origScrollIntoView;
    HTMLElement.prototype.focus       = origFocus;

    // Async guard: snap back if Shopify deferred a scroll via
    // setTimeout or rAF (runs for 400ms then stops watching)
    const snapBack = () => {
      if (!locked) return;
      if (scrollEl && Math.round(scrollEl.scrollTop) !== Math.round(savedY)) {
      scrollEl.scrollTop = savedY;
    } else if (!scrollEl && Math.round(window.scrollY) !== Math.round(savedY)) {
      window.scrollTo({ top: savedY, behavior: "instant" });
    }
    };

    requestAnimationFrame(snapBack);
    requestAnimationFrame(() => requestAnimationFrame(snapBack));
    setTimeout(snapBack, 50);
    setTimeout(snapBack, 150);
    setTimeout(() => { locked = false; }, 400);
  }

  function syncFromDOM() {
    const val = readVariantFromDOM();
    if (val === null) return;
    const newFoil = val === "foil";
    if (newFoil === isFoil) return;
    isFoil = newFoil;
    updateFoilUI();
  }

  function updateFoilUI() {
    card.classList.toggle("is-foil", isFoil);
    if (toggleInput) toggleInput.checked = isFoil;
    if (lblReg)  lblReg.style.color  = isFoil ? "rgba(255,255,255,0.35)" : "#ffffff";
    if (lblFoil) lblFoil.style.color = isFoil ? "var(--gold, #c9a84c)"   : "rgba(255,255,255,0.35)";
  }

  /* ── build viewer ────────────────────────────── */

  const infoWrapper = document.querySelector(".product__info-wrapper");
  if (!infoWrapper) return;

  const viewer = document.createElement("div");
  viewer.className = "token-card-viewer";

  const scene = document.createElement("div");
  scene.className = "token-card-scene";

  const card = document.createElement("div");
  card.className = "token-card";
  card.style.setProperty("--mx",  "-100%");
  card.style.setProperty("--my",  "-100%");
  card.style.setProperty("--bgx", "50%");
  card.style.setProperty("--bgy", "50%");

  const frontImg = document.createElement("img");
  frontImg.src       = images[0];
  frontImg.alt       = "Token card front";
  frontImg.className = "token-card__front";

  const backImg = document.createElement("img");
  backImg.className = "token-card__back";
  if (images[1]) { backImg.src = images[1]; backImg.alt = "Token card back"; }

  const gloss = document.createElement("div");
  gloss.className = "token-card__gloss";

  const holo = document.createElement("div");
  holo.className = "token-card__holo";
  if (isSafari) holo.classList.add("safari-holo");

  card.appendChild(frontImg);
  if (images[1]) card.appendChild(backImg);
  card.appendChild(gloss);
  card.appendChild(holo);
  scene.appendChild(card);
  viewer.appendChild(scene);

  applyMask(false);

  /* ── controls pill ───────────────────────────── */

  let toggleInput = null, lblReg = null, lblFoil = null;

  if (images.length > 1) {

    if (!document.getElementById("token-controls-style")) {
      const s = document.createElement("style");
      s.id = "token-controls-style";
      s.textContent = `
        /* Single pill wrapping flip + foil toggle */
        .token-controls-pill {
          display: inline-flex;
          align-items: center;
          gap: 0;
          margin-top: 16px;
          height: 42px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 40px;
          overflow: hidden;
          @media (max-width: 749px) {
  .bundle-viewer {
    overflow: visible;
  }
  .product__media-wrapper {
    overflow: visible !important;
  }
}
        }

        /* Flip button — left segment */
        .token-flip-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 42px;
          height: 100%;
          background: transparent;
          border: none;
          border-right: 1px solid rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.55);
          cursor: pointer;
          flex-shrink: 0;
          transition: color 0.2s, background 0.2s;
        }
        .token-flip-btn:hover {
          color: #ffffff;
          background: rgba(255,255,255,0.05);
        }

        /* Foil section — right segment */
        .token-foil-section {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 0 14px;
          height: 100%;
        }

        .token-foil-lbl {
          font-family: 'Jost', 'Instrument Sans', sans-serif;
          font-size: 1.05rem;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          transition: color 0.2s;
          user-select: none;
          cursor: pointer;
          line-height: 1;
        }

        /* Toggle */
        .token-foil-toggle {
          position: relative;
          width: 38px;
          height: 20px;
          cursor: pointer;
          flex-shrink: 0;
          display: block;
        }
        .token-foil-toggle input {
          position: absolute;
          opacity: 0;
          width: 0;
          height: 0;
        }
        .token-foil-track {
          position: absolute;
          inset: 0;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 10px;
          transition: background 0.25s ease, border-color 0.25s ease;
        }

        /* Thumb — matches landing page .tl-thumb exactly */
        .token-foil-thumb {
          position: absolute;
          top: 3px;
          left: 3px;
          width: 14px;
          height: 14px;
          background: rgba(255,255,255,0.9);
          border-radius: 50%;
          box-shadow:
            0 1px 3px rgba(0,0,0,0.4),
            0 0 0 1px rgba(255,255,255,0.08) inset;
          transition:
            transform 0.35s cubic-bezier(.34,1.45,.64,1),
            background 0.25s ease;
          pointer-events: none;
        }
        .token-foil-toggle input:checked ~ .token-foil-track {
          background: rgba(201,168,76,0.18);
          border-color: rgba(201,168,76,0.45);
        }
        .token-foil-toggle input:checked ~ .token-foil-thumb {
          transform: translateX(18px);
          background: var(--gold, #c9a84c);
        }
        .token-foil-toggle:hover .token-foil-thumb {
          box-shadow:
            0 1px 3px rgba(0,0,0,0.4),
            0 0 6px rgba(201,168,76,0.25),
            0 0 0 1px rgba(255,255,255,0.08) inset;
        }
      `;
      document.head.appendChild(s);
    }

    /* pill */
    const pill = document.createElement("div");
    pill.className = "token-controls-pill";

    /* flip button */
    const flipBtn = document.createElement("button");
    flipBtn.className = "token-flip-btn";
    flipBtn.title     = "Flip card";
    flipBtn.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
           viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
        <path d="M3 3v5h5"/>
      </svg>`;

    flipBtn.addEventListener("click", e => {
      e.stopPropagation();
      isFlipped = !isFlipped;
      card.style.transition = "transform 0.6s ease";
      card.style.transform  = `rotateY(${isFlipped ? 180 : 0}deg)`;
      setTimeout(() => applyMask(isFlipped), 300);
    });

    card.addEventListener("click",  e => e.stopPropagation());
    scene.addEventListener("click", e => e.stopPropagation());

    /* foil section */
    const foilSection = document.createElement("div");
    foilSection.className = "token-foil-section";

    lblReg = document.createElement("span");
    lblReg.className   = "token-foil-lbl";
    lblReg.textContent = "Regular";
    lblReg.style.color = "#ffffff";
    lblReg.addEventListener("click", () => { if (isFoil) activateFoil(false); });

    const toggleWrap = document.createElement("label");
    toggleWrap.className = "token-foil-toggle";

    toggleInput = document.createElement("input");
    toggleInput.type    = "checkbox";
    toggleInput.checked = isFoil;

    const track = document.createElement("div");
    track.className = "token-foil-track";

    const thumb = document.createElement("div");
    thumb.className = "token-foil-thumb";

    /* order matters: input → track → thumb so CSS sibling selectors work */
    toggleWrap.appendChild(toggleInput);
    toggleWrap.appendChild(track);
    toggleWrap.appendChild(thumb);

    lblFoil = document.createElement("span");
    lblFoil.className   = "token-foil-lbl";
    lblFoil.textContent = "Foil";
    lblFoil.style.color = "rgba(255,255,255,0.35)";
    lblFoil.addEventListener("click", () => { if (!isFoil) activateFoil(true); });

    toggleInput.addEventListener("change", e => activateFoil(e.target.checked));

    foilSection.appendChild(lblReg);
    foilSection.appendChild(toggleWrap);
    foilSection.appendChild(lblFoil);

    pill.appendChild(flipBtn);
    pill.appendChild(foilSection);
    viewer.appendChild(pill);
  }

  /* ── activate foil ───────────────────────────── */
function activateFoil(on) {
  isFoil = on;
  updateFoilUI();
  
  const scrollEl = document.querySelector('.gradient');
  if (scrollEl) {
    const savedTop = scrollEl.scrollTop;
    const start = Date.now();
    const lock = () => {
      scrollEl.scrollTop = savedTop;
      if (Date.now() - start < 600) requestAnimationFrame(lock);
    };
    requestAnimationFrame(lock);
  }
  
  setVariantInDOM(on ? "foil" : "regular");
}

  /* ── mount viewer ────────────────────────────── */
  const mediaWrapper = document.querySelector(".product__media-wrapper");
  if (mediaWrapper) {
    mediaWrapper.innerHTML = "";
    mediaWrapper.appendChild(viewer);
    mediaWrapper.style.display = "block";
  } else {
    infoWrapper.parentNode.insertBefore(viewer, infoWrapper);
  }

  /* ── mouse tilt ──────────────────────────────── */
  scene.addEventListener("mousemove", e => {
    const rect = scene.getBoundingClientRect();
    const mx   = ((e.clientX - rect.left) / rect.width)  * 100;
    const my   = ((e.clientY - rect.top)  / rect.height) * 100;
    const rotY = ((mx - 50) / 50) * 15;
    const rotX = -((my - 50) / 50) * 15;
    const flipDeg = isFlipped ? 180 : 0;
    card.style.transition = "transform 0.1s ease";
    card.style.transform  = `rotateY(${flipDeg + rotY}deg) rotateX(${rotX}deg)`;
    card.style.setProperty("--mx",         mx + "%");
    card.style.setProperty("--my",         my + "%");
    card.style.setProperty("--bgx",        mx + "%");
    card.style.setProperty("--bgy",        my + "%");
    card.style.setProperty("--holo-angle", (rotY * 2 + 135) + "deg");
    card.classList.add("active");
  });

  scene.addEventListener("mouseleave", () => {
    const flipDeg = isFlipped ? 180 : 0;
    card.style.transition = "transform 0.4s ease";
    card.style.transform  = `rotateY(${flipDeg}deg) rotateX(0deg)`;
    card.classList.remove("active");
  });

  /* ── touch tilt ──────────────────────────────── */
  let touchActive = false;

  scene.addEventListener("touchstart", e => {
    if (e.target.closest(".token-controls-pill")) return;
    e.preventDefault();
    touchActive = true;
    setTimeout(() => card.classList.add("active"), 50);
  }, { passive: false });

  scene.addEventListener("touchmove", e => {
    if (!touchActive) return;
    e.preventDefault();
    const touch = e.touches[0];
    const rect  = scene.getBoundingClientRect();
    const mx    = Math.min(98, Math.max(2, ((touch.clientX - rect.left) / rect.width)  * 100));
    const my    = Math.min(98, Math.max(2, ((touch.clientY - rect.top)  / rect.height) * 100));
    const rotY  = ((mx - 50) / 50) * 20;
    const rotX  = -((my - 50) / 50) * 20;
    const flipDeg = isFlipped ? 180 : 0;
    card.style.transition = "none";
    card.style.transform  = `rotateY(${flipDeg + rotY}deg) rotateX(${rotX}deg)`;
    card.style.setProperty("--mx",         mx + "%");
    card.style.setProperty("--my",         my + "%");
    card.style.setProperty("--bgx",        mx + "%");
    card.style.setProperty("--bgy",        my + "%");
    card.style.setProperty("--holo-angle", (rotY * 2 + 135) + "deg");
  }, { passive: false });

  scene.addEventListener("touchend", () => {
    touchActive = false;
    const flipDeg = isFlipped ? 180 : 0;
    card.style.transition = "transform 0.4s ease";
    card.style.transform  = `rotateY(${flipDeg}deg) rotateX(0deg)`;
    card.style.setProperty("--mx",  "-100%");
    card.style.setProperty("--my",  "-100%");
    card.style.setProperty("--bgx", "50%");
    card.style.setProperty("--bgy", "50%");
    card.classList.remove("active");
  });

  /* ── variant → toggle sync ───────────────────── */
  document.addEventListener("change", e => {
    if (e.target === toggleInput) return;
    syncFromDOM();
  });
  document.addEventListener("optionValueSelectionChange", syncFromDOM);

  syncFromDOM();
});

/* ===================================== */
/* BUNDLE VIEWER                        */
/* ===================================== */

document.addEventListener("DOMContentLoaded", function () {

  const productEl = document.querySelector("[data-product-id]");
  if (!productEl) return;

  const productType = document.querySelector('meta[name="product-type"]')?.content?.toLowerCase();
  if (productType !== "bundle") return;

  document.body.classList.add("bundle-product-page");

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  const TOKENS = [
    {
      name: "Dragon // Bird",
      front:     "https://danieltaylor.hu/cdn/shop/files/4_5796a8dd-e62d-4a6a-8cf3-670570f56f95.png?v=1773303038&width=800",
      back:      "https://danieltaylor.hu/cdn/shop/files/3_b805db23-799b-4406-8e4d-902b64ff4aa4.png?v=1773303038&width=800",
      frontMask: "https://danieltaylor.hu/cdn/shop/files/dragon_foil_0447bb81-c236-4704-8129-ed03b90a2031.png?v=1773324439&width=800",
      backMask:  "https://danieltaylor.hu/cdn/shop/files/bird_foil.png?v=1773324439&width=800"
    },
    {
      name: "Vampire // Treasure",
      front:     "https://danieltaylor.hu/cdn/shop/files/1_afc55173-c11c-43ef-91e0-8489e2231fb2.png?v=1773330355&width=800",
      back:      "https://danieltaylor.hu/cdn/shop/files/2_e4ae8228-9d1f-450f-917c-e5e12fe0d34b.png?v=1773330355&width=800",
      frontMask: "https://danieltaylor.hu/cdn/shop/files/3_260eb27d-4f97-475f-99f0-92c717839b92.png?v=1773330355&width=800",
      backMask:  "https://danieltaylor.hu/cdn/shop/files/4_2c32d76c-b1db-4bcf-bb4d-c39abed2018c.png?v=1773330355&width=800"
    },
    {
      name: "Goblin // Beast",
      front:     "https://danieltaylor.hu/cdn/shop/files/5_eeb12bfb-3b79-4fd1-83d3-3ef3eb2086dd.png?v=1773330397&width=800",
      back:      "https://danieltaylor.hu/cdn/shop/files/6_7e33e9cd-3216-4c8a-a0ca-a8df8a8812e9.png?v=1773330397&width=800",
      frontMask: "https://danieltaylor.hu/cdn/shop/files/5_07e739ec-eefe-439c-ae33-bb5ebbdbb2ab.png?v=1773330397&width=800",
      backMask:  "https://danieltaylor.hu/cdn/shop/files/6_48190b35-d86f-47de-8c68-175bb85a284e.png?v=1773330397&width=800"
    },
    {
      name: "Faerie // Copy",
      front:     "https://danieltaylor.hu/cdn/shop/files/7_fb99d192-099d-470d-9859-c2e0a0fc2c97.png?v=1773330441&width=800",
      back:      "https://danieltaylor.hu/cdn/shop/files/8_0d3f75a3-e407-4b7b-80ff-e8948d3c20be.png?v=1773330441&width=800",
      frontMask: "https://danieltaylor.hu/cdn/shop/files/7_25129d3f-97bb-413f-917e-474dfcbf04e2.png?v=1773330441&width=800",
      backMask:  "https://danieltaylor.hu/cdn/shop/files/8_5bfdff70-0d72-4bb7-8aab-128567174e3a.png?v=1773330441&width=800"
    }
  ];

  /* ── state ───────────────────────────────────── */
  let topIndex  = 0;
  let isFoil    = false;
  let flipped   = [false, false, false, false];
  let touchActive = false;

  /* ── fan offsets — matches landing page exactly ─ */
  
const isMobile = window.innerWidth < 750;

const FAN = isMobile ? [
  { x:   0, y:  0, rot:  0,  z: 4, opacity: 1 },
  { x: -70, y: 14, rot: -8,  z: 3, opacity: 1 },
  { x:  70, y: 14, rot:  8,  z: 2, opacity: 1 },
  { x:   0, y: 38, rot:  0,  z: 1, opacity: 0 }
] : [
  { x:   0, y:  0, rot:  0,  z: 4, opacity: 1 },
  { x: -90, y: 20, rot: -12, z: 3, opacity: 1 },
  { x:  90, y: 20, rot:  12, z: 2, opacity: 1 },
  { x:   0, y: 60, rot:  0,  z: 1, opacity: 0 }
];
  /* ── inject styles once ──────────────────────── */
  if (!document.getElementById("bundle-viewer-style")) {
    const s = document.createElement("style");
    s.id = "bundle-viewer-style";
    s.textContent = `
      .bundle-viewer {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 2rem 0;
      }

      /* Fan stage — same card size as single token viewer */
      .bundle-fan-stage {
        position: relative;
        width: var(--card-w, 300px);
        height: var(--card-h, 419px);
        margin: 0 auto 24px;
        /* extra room for the fan spread */
        overflow: visible;
      }

      .bundle-fan-card {
        position: absolute;
        inset: 0;
        width: var(--card-w, 300px);
        height: var(--card-h, 419px);
        border-radius: 4.75% / 3.5%;
        transform-style: preserve-3d;
        transition: transform 0.5s cubic-bezier(0.4,0,0.2,1);
        cursor: pointer;
        box-shadow: 0 8px 32px rgba(0,0,0,0.7), 0 2px 8px rgba(0,0,0,0.4);
      }

      .bundle-fan-card img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 4.75% / 3.5%;
        display: block;
        pointer-events: none;
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
      }

      .bundle-fan-card img.bundle-fan-back {
        transform: rotateY(180deg);
      }

      .bundle-fan-card.is-flipped {
        /* individual card flip handled via inline style */
      }

      .bundle-fan-gloss {
        position: absolute; inset: 0; z-index: 2;
        border-radius: 4.75% / 3.5%;
        background: radial-gradient(
          farthest-corner ellipse at var(--mx, 50%) var(--my, 50%),
          rgba(255,255,255,0.28) 0%,
          rgba(255,255,255,0.04) 50%,
          transparent 70%
        );
        pointer-events: none; opacity: 0; transition: opacity 0.3s;
      }
      .bundle-fan-card.active .bundle-fan-gloss { opacity: 1; }

      .bundle-fan-holo {
        position: absolute; inset: 0; z-index: 3;
        border-radius: 4.75% / 3.5%;
        pointer-events: none;
        opacity: 0; transition: opacity 0.4s;
        background-image: repeating-linear-gradient(
          var(--holo-angle, -22deg),
          hsla(283,49%,60%,0.85) 0%,
          hsla(2,74%,59%,0.85)   5%,
          hsla(53,67%,53%,0.85)  10%,
          hsla(93,56%,52%,0.85)  15%,
          hsla(176,38%,50%,0.85) 20%,
          hsla(228,100%,77%,0.85)25%,
          hsla(283,49%,61%,0.85) 30%
        );
        background-size: 300% 400%;
        background-position: var(--bgx,50%) var(--bgy,50%);
        mix-blend-mode: color-dodge;
        filter: brightness(0.45) contrast(2.4) saturate(0.9);
        animation: bundleHoloShift 8s ease-in-out infinite;
      }
      @keyframes bundleHoloShift {
        0%  { background-position: 0%   0%;   --holo-angle: 115deg; }
        50% { background-position: 100% 100%; --holo-angle: 155deg; }
        100%{ background-position: 0%   0%;   --holo-angle: 115deg; }
      }
      .bundle-fan-card.is-foil .bundle-fan-holo { opacity: 0.35 !important; }
      .bundle-fan-card.is-foil.active .bundle-fan-holo { opacity: 0.6 !important; }

      /* Pill — identical to single token product */
      .bundle-controls-pill {
        display: inline-flex;
        align-items: center;
        gap: 0;
        margin-top: 8px;
        height: 42px;
        background: rgba(255,255,255,0.03);
        border: 1px solid rgba(255,255,255,0.1);
        border-radius: 40px;
        overflow: hidden;
      }
      .bundle-pill-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 42px;
        height: 100%;
        background: transparent;
        border: none;
        border-right: 1px solid rgba(255,255,255,0.08);
        color: rgba(255,255,255,0.55);
        cursor: pointer;
        flex-shrink: 0;
        transition: color 0.2s, background 0.2s;
      }
      .bundle-pill-btn:last-child { border-right: none; border-left: 1px solid rgba(255,255,255,0.08); }
      .bundle-pill-btn:hover { color: #ffffff; background: rgba(255,255,255,0.05); }

      .bundle-foil-section {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 0 14px;
        height: 100%;
      }
      .bundle-foil-lbl {
        font-family: 'Jost', 'Instrument Sans', sans-serif;
        font-size: 1.05rem;
        font-weight: 500;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        transition: color 0.2s;
        user-select: none;
        cursor: pointer;
        line-height: 1;
      }
      .bundle-foil-toggle {
        position: relative;
        width: 38px; height: 20px;
        cursor: pointer; flex-shrink: 0; display: block;
      }
      .bundle-foil-toggle input {
        position: absolute; opacity: 0; width: 0; height: 0;
      }
      .bundle-foil-track {
        position: absolute; inset: 0;
        background: rgba(255,255,255,0.05);
        border: 1px solid rgba(255,255,255,0.15);
        border-radius: 10px;
        transition: background 0.25s ease, border-color 0.25s ease;
      }
      .bundle-foil-thumb {
        position: absolute;
        top: 3px; left: 3px;
        width: 14px; height: 14px;
        background: rgba(255,255,255,0.9);
        border-radius: 50%;
        box-shadow: 0 1px 3px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.08) inset;
        transition: transform 0.35s cubic-bezier(.34,1.45,.64,1), background 0.25s ease;
        pointer-events: none;
      }
      .bundle-foil-toggle input:checked ~ .bundle-foil-track {
        background: rgba(201,168,76,0.18);
        border-color: rgba(201,168,76,0.45);
      }
      .bundle-foil-toggle input:checked ~ .bundle-foil-thumb {
        transform: translateX(18px);
        background: var(--gold, #c9a84c);
      }
      .bundle-foil-toggle:hover .bundle-foil-thumb {
        box-shadow: 0 1px 3px rgba(0,0,0,0.4), 0 0 6px rgba(201,168,76,0.25), 0 0 0 1px rgba(255,255,255,0.08) inset;
      }

      /* Card name label */
      .bundle-card-label {
        font-family: 'Jost', 'Instrument Sans', sans-serif;
        font-size: 1rem;
        letter-spacing: 0.15em;
        text-transform: uppercase;
        color: rgba(255,255,255,0.35);
        text-align: center;
        margin-top: 12px;
        min-height: 1em;
      }

     @media (max-width: 749px) {
  .bundle-fan-stage {
    width: 260px;
    height: 400px;
    margin-left: auto;
    margin-right: auto;
    display: block;
  }
  .bundle-fan-card {
    width: 260px;
    height: 363px;
  }
}
}
    `;
    document.head.appendChild(s);
  }

  /* ── build viewer ────────────────────────────── */
  const infoWrapper = document.querySelector(".product__info-wrapper");
  if (!infoWrapper) return;

  const viewer = document.createElement("div");
  viewer.className = "bundle-viewer";

  /* fan stage */
  const stage = document.createElement("div");
  stage.className = "bundle-fan-stage";

  /* build cards — back to front so topIndex card is last in DOM */
  const cardEls = TOKENS.map((token, i) => {
    const card = document.createElement("div");
    card.className = "bundle-fan-card";

    const frontImg = document.createElement("img");
    frontImg.src = token.front; frontImg.alt = token.name;
    frontImg.className = "bundle-fan-front";

    const backImg = document.createElement("img");
    backImg.src = token.back; backImg.alt = token.name + " back";
    backImg.className = "bundle-fan-back";

    const gloss = document.createElement("div");
    gloss.className = "bundle-fan-gloss";

    const holo = document.createElement("div");
    holo.className = "bundle-fan-holo";
    if (isSafari) holo.classList.add("safari-holo");

    card.appendChild(frontImg);
    card.appendChild(backImg);
    card.appendChild(gloss);
    card.appendChild(holo);
    stage.appendChild(card);

    return { card, frontImg, backImg, gloss, holo };
  });

  viewer.appendChild(stage);

  /* ── pill controls ───────────────────────────── */
  const pill = document.createElement("div");
  pill.className = "bundle-controls-pill";

  /* next button */
  const nextBtn = document.createElement("button");
  nextBtn.className = "bundle-pill-btn";
  nextBtn.title = "Next card";
  nextBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>`;

  /* flip button */
  const flipBtn = document.createElement("button");
  flipBtn.className = "bundle-pill-btn";
  flipBtn.title = "Flip card";
  flipBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>`;

  /* foil section */
  const foilSection = document.createElement("div");
  foilSection.className = "bundle-foil-section";

  const lblReg = document.createElement("span");
  lblReg.className = "bundle-foil-lbl";
  lblReg.textContent = "Regular";
  lblReg.style.color = "#ffffff";

  const toggleWrap = document.createElement("label");
  toggleWrap.className = "bundle-foil-toggle";

  const toggleInput = document.createElement("input");
  toggleInput.type = "checkbox";
  toggleInput.checked = false;

  const track = document.createElement("div");
  track.className = "bundle-foil-track";

  const thumb = document.createElement("div");
  thumb.className = "bundle-foil-thumb";

  toggleWrap.appendChild(toggleInput);
  toggleWrap.appendChild(track);
  toggleWrap.appendChild(thumb);

  const lblFoil = document.createElement("span");
  lblFoil.className = "bundle-foil-lbl";
  lblFoil.textContent = "Foil";
  lblFoil.style.color = "rgba(255,255,255,0.35)";

  foilSection.appendChild(lblReg);
  foilSection.appendChild(toggleWrap);
  foilSection.appendChild(lblFoil);

  pill.appendChild(nextBtn);
  pill.appendChild(foilSection);
  pill.appendChild(flipBtn);
  viewer.appendChild(pill);

  /* ── mount viewer ────────────────────────────── */
  const mediaWrapper = document.querySelector(".product__media-wrapper");
  if (mediaWrapper) {
    mediaWrapper.innerHTML = "";
    mediaWrapper.appendChild(viewer);
    mediaWrapper.style.display = "block";
  } else {
    infoWrapper.parentNode.insertBefore(viewer, infoWrapper);
  }

  /* ── helpers ─────────────────────────────────── */
  function applyMask(idx) {
    const { holo } = cardEls[idx];
    const token = TOKENS[idx];
    const mask = flipped[idx] ? token.backMask : token.frontMask;
    holo.style.maskImage          = `url(${mask})`;
    holo.style.webkitMaskImage    = `url(${mask})`;
    holo.style.maskSize           = "100% 100%";
    holo.style.webkitMaskSize     = "100% 100%";
    holo.style.maskMode           = "alpha";
    holo.style.webkitMaskMode     = "alpha";
    holo.style.transform          = flipped[idx] ? "scaleX(-1)" : "scaleX(1)";
  }

  function getFlipDeg(idx) {
    return flipped[idx] ? 180 : 0;
  }

  function applyFan(animate) {
  cardEls.forEach(({ card }, i) => {
    const pos = ((i - topIndex) % 4 + 4) % 4;
    const f   = FAN[pos];
    card.style.transition    = animate
      ? "transform 0.5s cubic-bezier(0.4,0,0.2,1), opacity 0.4s ease"
      : "none";
    card.style.transform     = `translate(${f.x}px, ${f.y}px) rotate(${f.rot}deg) rotateY(${getFlipDeg(i)}deg)`;
    card.style.zIndex        = f.z;
    card.style.opacity       = f.opacity;
card.style.pointerEvents = f.opacity === 0 ? "none" : "auto";
// Kill holo on hidden card so it doesn't bleed through
const { holo } = cardEls[i];
if (f.opacity === 0) {
  holo.style.setProperty("opacity", "0", "important");
  holo.style.animationPlayState = "paused";
} else {
  holo.style.removeProperty("opacity");
  holo.style.animationPlayState = "";
}
  });
}


  function updateFoilUI() {
    cardEls.forEach(({ card }) => card.classList.toggle("is-foil", isFoil));
    TOKENS.forEach((_, i) => applyMask(i));
    lblReg.style.color  = isFoil ? "rgba(255,255,255,0.35)" : "#ffffff";
    lblFoil.style.color = isFoil ? "var(--gold, #c9a84c)"   : "rgba(255,255,255,0.35)";
    toggleInput.checked = isFoil;
  }

  function readVariantFromDOM() {
    const radio = document.querySelector(".product-form__input input[type='radio']:checked");
    if (radio) return (radio.value || radio.dataset.value || "").toLowerCase();
    return null;
  }

  function setVariantInDOM(target) {
    const radios = document.querySelectorAll(".product-form__input input[type='radio']");
    for (const r of radios) {
      const val = (r.value || r.dataset.value || "").toLowerCase();
      if (val === target && !r.checked) {
        r.checked = true;
        r.dispatchEvent(new Event("change", { bubbles: true }));
        return;
      }
    }
  }

  function syncFromDOM() {
    const val = readVariantFromDOM();
    if (val === null) return;
    const newFoil = val === "foil";
    if (newFoil === isFoil) return;
    isFoil = newFoil;
    updateFoilUI();
  }

  /* ── scroll-lock fix (same as single token) ──── */
  function activateFoil(on) {
    isFoil = on;
    updateFoilUI();

    const scrollEl = document.querySelector(".gradient");
    if (scrollEl) {
      const savedTop = scrollEl.scrollTop;
      const start    = Date.now();
      const lock     = () => {
        scrollEl.scrollTop = savedTop;
        if (Date.now() - start < 600) requestAnimationFrame(lock);
      };
      requestAnimationFrame(lock);
    }

    setVariantInDOM(on ? "foil" : "regular");
  }

  /* ── events ──────────────────────────────────── */

  /* next card */

 nextBtn.addEventListener("click", () => {
  const { card } = cardEls[topIndex];
  card.style.transition = "transform 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.25s ease";
  card.style.transform  = "translate(-40px, -50px) rotate(-15deg) scale(0.85)";
  card.style.opacity    = "0";
  setTimeout(() => {
    topIndex = (topIndex + 1) % 4;
    applyFan(true);
  }, 180);
});

  /* flip top card */
  flipBtn.addEventListener("click", () => {
    flipped[topIndex] = !flipped[topIndex];
    applyFan(true);
    setTimeout(() => applyMask(topIndex), 300);

  });

  /* tap a back card to bring it to front */
  cardEls.forEach(({ card }, i) => {
    card.addEventListener("click", () => {
      const pos = ((i - topIndex) % 4 + 4) % 4;
      if (pos === 0) {
        /* tap top card = flip */
        flipped[i] = !flipped[i];
        applyFan(true);
        setTimeout(() => applyMask(i), 300);
    
      } else {
        topIndex = i;
        applyFan(true);
      }
    });
  });

  /* foil toggle */
  toggleInput.addEventListener("change", e => activateFoil(e.target.checked));
  lblReg.addEventListener("click",  () => { if (isFoil)  activateFoil(false); });
  lblFoil.addEventListener("click", () => { if (!isFoil) activateFoil(true);  });

  /* variant selector → toggle sync */
  document.addEventListener("change", e => {
    if (e.target === toggleInput) return;
    syncFromDOM();
  });

  /* ── mouse tilt on top card ──────────────────── */
  let isOverTop = false;

  document.addEventListener("mousemove", e => {
    const { card, holo } = cardEls[topIndex];
    const rect   = card.getBoundingClientRect();
    const inside = e.clientX >= rect.left && e.clientX <= rect.right &&
                   e.clientY >= rect.top  && e.clientY <= rect.bottom;

    if (!inside) {
      if (isOverTop) {
        isOverTop = false;
        const pos = FAN[0];
        card.style.transition = "transform 0.4s ease";
        card.style.transform  = `translate(${pos.x}px, ${pos.y}px) rotate(${pos.rot}deg) rotateY(${getFlipDeg(topIndex)}deg) rotateX(0deg)`;
        card.classList.remove("active");
        holo.style.animation = "";
        holo.style.backgroundPosition = "";
      }
      return;
    }

    isOverTop = true;
    const mx   = ((e.clientX - rect.left) / rect.width)  * 100;
    const my   = ((e.clientY - rect.top)  / rect.height) * 100;
    const rotY = ((mx - 50) / 50) * 15;
    const rotX = -((my - 50) / 50) * 15;
    const pos  = FAN[0];

    card.style.transition = "transform 0.1s ease";
    card.style.transform  = `translate(${pos.x}px, ${pos.y}px) rotate(${pos.rot}deg) rotateY(${getFlipDeg(topIndex) + rotY}deg) rotateX(${rotX}deg)`;
    card.style.setProperty("--mx",         mx + "%");
    card.style.setProperty("--my",         my + "%");
    card.style.setProperty("--bgx",        mx + "%");
    card.style.setProperty("--bgy",        my + "%");
    card.style.setProperty("--holo-angle", (rotY * 2 + 135) + "deg");
    card.classList.add("active");
    holo.style.animation = "none";
    holo.style.backgroundPosition = `${mx}% ${my}%`;
  });

  /* ── touch tilt on top card ──────────────────── */
  stage.addEventListener("touchstart", e => {
    if (e.target.closest(".bundle-controls-pill")) return;
    e.preventDefault();
    touchActive = true;
    const { holo } = cardEls[topIndex];
    holo.style.animation = "none";
    setTimeout(() => cardEls[topIndex].card.classList.add("active"), 50);
  }, { passive: false });

  stage.addEventListener("touchmove", e => {
    if (!touchActive) return;
    e.preventDefault();
    const touch = e.touches[0];
    const { card, holo } = cardEls[topIndex];
    const rect  = card.getBoundingClientRect();
    const mx    = Math.min(98, Math.max(2, ((touch.clientX - rect.left) / rect.width)  * 100));
    const my    = Math.min(98, Math.max(2, ((touch.clientY - rect.top)  / rect.height) * 100));
    const rotY  = ((mx - 50) / 50) * 20;
    const rotX  = -((my - 50) / 50) * 20;
    const pos   = FAN[0];
    card.style.transition = "none";
    card.style.transform  = `translate(${pos.x}px, ${pos.y}px) rotate(${pos.rot}deg) rotateY(${getFlipDeg(topIndex) + rotY}deg) rotateX(${rotX}deg)`;
    card.style.setProperty("--mx",         mx + "%");
    card.style.setProperty("--my",         my + "%");
    card.style.setProperty("--bgx",        mx + "%");
    card.style.setProperty("--bgy",        my + "%");
    card.style.setProperty("--holo-angle", (rotY * 2 + 135) + "deg");
    holo.style.backgroundPosition = `${mx}% ${my}%`;
  }, { passive: false });

  stage.addEventListener("touchend", () => {
    touchActive = false;
    const { card, holo } = cardEls[topIndex];
    const pos = FAN[0];
    card.style.transition = "transform 0.4s ease";
    card.style.transform  = `translate(${pos.x}px, ${pos.y}px) rotate(${pos.rot}deg) rotateY(${getFlipDeg(topIndex)}deg) rotateX(0deg)`;
    card.style.setProperty("--mx",  "-100%");
    card.style.setProperty("--my",  "-100%");
    card.style.setProperty("--bgx", "50%");
    card.style.setProperty("--bgy", "50%");
    card.classList.remove("active");
    holo.style.animation = "";
    holo.style.backgroundPosition = "";
  });

  /* ── init ────────────────────────────────────── */
  applyFan(false);
  TOKENS.forEach((_, i) => applyMask(i));
  updateFoilUI();
  syncFromDOM();
});


